import type React from 'react';
import { useMemo } from 'react';
import { useAppSelector } from '../../hooks';
import { styled } from '@mui/material/styles';

//Redux

import { motion } from 'motion/react';
import {
  selectError,
  selectWidgetMode,
  selectDismissableNotification,
  selectIsDelaying,
  selectVisibleWidgetComponents,
  selectAreLoadingDotsVisible,
  selectIsFreeFormInputVisible,
} from 'reducers/workflows';

// Components
import ScrollableMessagesContainer from '../ScrollableMessagesContainer';
import CustomerSatisfactionForm from 'components/CustomerSatisfactionForm/CustomerSatisfactionForm';
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';

//Types
import type { WidgetComponent } from 'api/types';

//Effects
import { useHandleScrollBehavior } from 'components/App/hooks';
import useTracing from 'components/App/hooks/useTracing';
import { FreeFormInput } from 'components/FreeFormInput';
import { selectIsAutonomousAgent } from 'reducers/config';
import useScrollToBottomOnFreeFormQueryChange from 'components/App/hooks/useScrollToBottomOnFreeFormQueryChange';
import ErrorBanner from 'components/ErrorBanner';
import AutonomousAgentInput from 'components/AutonomousAgentInput';
import LiveChatInput from 'components/LiveChatInput/LiveChatInput';
import useAnnounceLoading from 'components/App/hooks/useAnnounceLoading';
import useFocusReceivedMessage from 'components/App/hooks/useFocusReceivedMessage';
import { useErrorBannerTrackingEvent } from 'components/App/hooks/useErrorBannerTrackingEvent';
import { useArtificialMessageDelay } from './useArtificialMessageDelay';
import { LoadingMessageWithDots } from 'components/LoadingMessageWithDots';
import {
  selectAreAgentTypingDotsVisible,
  selectIsLiveChatInputVisible,
} from 'reducers/live-chat';
import { GenericStepComponent } from './GenericStepComponent';
import { Box } from '@mui/material';
import { DismissableNotificationToast } from 'components/DismissableNotificationToast';
import { LIVE_CHAT_BACKGROUND_IMAGE_CSS } from 'embed/constants';

const WorkflowsPage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const error = useAppSelector(selectError);
  const widgetMode = useAppSelector(selectWidgetMode);
  const isAutonomousAgent = useAppSelector(selectIsAutonomousAgent);
  const dismissableNotification = useAppSelector(selectDismissableNotification);
  const isDelaying = useAppSelector(selectIsDelaying);
  const visibleWidgetComponents = useAppSelector(selectVisibleWidgetComponents);
  const areLoadingDotsVisible = useAppSelector(selectAreLoadingDotsVisible);
  const areAgentTypingDotsVisible = useAppSelector(
    selectAreAgentTypingDotsVisible,
  );
  const isFreeFormInputVisible = useAppSelector(selectIsFreeFormInputVisible);
  const isLiveChatInputVisible = useAppSelector(selectIsLiveChatInputVisible);

  // Effects
  useTracing();
  useScrollToBottomOnFreeFormQueryChange();
  useArtificialMessageDelay();
  useHandleScrollBehavior();
  useAnnounceLoading();
  useFocusReceivedMessage();
  useErrorBannerTrackingEvent();

  const getInputComponent = () => {
    if (widgetMode === 'liveChat') {
      if (!isLiveChatInputVisible) {
        return null;
      }

      return <LiveChatInput />;
    }

    if (isFreeFormInputVisible) {
      return <FreeFormInput isDelaying={isDelaying} />;
    } else if (isAutonomousAgent) {
      return <AutonomousAgentInput />;
    }

    return null;
  };

  const animate = useMemo(
    () => ({
      y: '0px',
    }),
    [],
  );
  const initial = useMemo(
    () => ({
      y: '30px',
    }),
    [],
  );
  const transition = useMemo(
    () => ({
      bounce: 0.35,
      duration: 0.4,
      type: 'spring',
    }),
    [],
  );
  const sx = useMemo(() => ({ overflowY: 'hidden' }), []);

  return (
    <ContentContainer>
      <CustomerSatisfactionForm />
      <DismissableNotificationToast notification={dismissableNotification} />
      <ErrorBanner errorData={error} />
      <ScrollableMessagesContainer>
        {visibleWidgetComponents.map(
          (widgetComponent: WidgetComponent, index: number) => {
            return (
              <GenericStepComponent
                index={index}
                isLastStep={index === visibleWidgetComponents.length - 1}
                key={index}
                widgetComponent={widgetComponent}
              />
            );
          },
        )}
        {(areLoadingDotsVisible || areAgentTypingDotsVisible) && (
          <Box sx={sx}>
            <motion.div
              animate={animate}
              initial={initial}
              transition={transition}
            >
              <LoadingMessageWithDots />
            </motion.div>
          </Box>
        )}
      </ScrollableMessagesContainer>
      {/* render free form input or autonomous agent input */}
      {getInputComponent()}
    </ContentContainer>
  );
};

const ContentContainer = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  & > * {
    padding-left: 8px;
    padding-right: 8px;
  }
`;

export const AgathaLoadingMessage = styled(AgathaMessage)`
  background-color: ${props => props.theme.palette.colors.slate[100]};
  ${props => props.isLiveChat && LIVE_CHAT_BACKGROUND_IMAGE_CSS};
  min-height: 0;
  width: 61px;
  height: 35px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  left: 0px;
  border-radius: 25px;
`;

export default WorkflowsPage;
